import { authHeaders } from '../../lib/authHeaders';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: any[];
  model: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cache_read_tokens: number | null;
  cache_write_tokens: number | null;
  cost_usd: string | null;
  tool_calls: any[];
  finish_reason: string | null;
  created_at: string;
}

export interface ThreadResponse {
  thread_id: string;
  messages: ChatMessage[];
  cost: {
    month_to_date_usd: number;
    soft_threshold_usd: number;
    hard_threshold_usd: number;
    soft_breached: boolean;
    hard_breached: boolean;
  };
}

export async function fetchThread(): Promise<ThreadResponse> {
  const r = await fetch('/api/mi/chat/thread', { credentials: 'include', headers: authHeaders() });
  if (!r.ok) {
    let detail = r.statusText;
    try { detail = (await r.json()).error ?? detail; } catch {}
    throw new Error(`${r.status} ${detail}`);
  }
  return r.json();
}

export type SsePayload =
  | { event: 'tool_start';  data: { tool_use_id: string; tool_name: string; input: any } }
  | { event: 'tool_end';    data: { tool_use_id: string; ok: boolean; error: string | null; result_preview: string; truncated_at: number | null } }
  | { event: 'final_text';  data: { text: string } }
  | { event: 'done';        data: { message_id: string; input_tokens: number; output_tokens: number; cache_read_tokens: number; cache_write_tokens: number; cost_usd: number; mtd_usd: number; soft_threshold_usd: number; hard_threshold_usd: number; finish_reason: string } }
  | { event: 'error';       data: { message: string } };

// Parses an SSE response stream and invokes onEvent for each event.
// Resolves when the stream ends or an `error` event fires.
export async function sendChatMessage(
  userText: string,
  onEvent: (e: SsePayload) => void,
  options?: { overrideHardCap?: boolean },
): Promise<void> {
  const r = await fetch('/api/mi/chat/send', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ user_text: userText, override_hard_cap: options?.overrideHardCap ?? false }),
  });
  if (!r.ok || !r.body) {
    let detail = r.statusText;
    try { detail = (await r.json()).error ?? detail; } catch {}
    throw new Error(`${r.status} ${detail}`);
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE events are separated by blank lines (\n\n).
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const parsed = parseSseEvent(rawEvent);
      if (parsed) onEvent(parsed);
    }
  }
}

function parseSseEvent(raw: string): SsePayload | null {
  let event = '';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  if (!event || dataLines.length === 0) return null;
  try {
    const data = JSON.parse(dataLines.join('\n'));
    return { event, data } as SsePayload;
  } catch {
    return null;
  }
}
